import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Column } from "types";
import { ReactNode, Suspense, useCallback, useState } from "react";
import { getValueAtPath, smallTableHeaderSx } from "utils";

import { Await } from "react-router";
import PanelTableSkeleton from "components/skeletons/PanelTableSkeleton";
import { useTranslation } from "react-i18next";
import { visuallyHidden } from "@mui/utils";

const MAX_PREVIEW_ROWS = 100;

export interface PanelTableProps<T extends object> {
  renderRow: (item: T, index: number) => ReactNode;
  columns: Column[];
  name: string;
  maxHeight?: number | string;
  size?: "small" | "medium";
  asyncData?: Promise<T[]>;
  data?: T[];
  searchQuery?: string;
  display?: "async" | "data";
  readonly?: boolean;
  hideNoOptionsText?: boolean;
}

export type SortConfig = {
  key: string;
  direction: "asc" | "desc";
};

const PanelTable = <T extends object>({
  renderRow,
  columns,
  maxHeight,
  size = "small",
  name,
  asyncData,
  data,
  searchQuery = "",
  display = "async",
  readonly = false,
  hideNoOptionsText = false,
}: PanelTableProps<T>): ReactNode => {
  const { t } = useTranslation();

  // sort
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "",
    direction: "asc",
  });
  const handleSort = (sortKey: string | undefined) => {
    if (!sortKey) return;
    setSortConfig((prevSortConfig) => ({
      key: sortKey,
      direction:
        prevSortConfig.key === sortKey && prevSortConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };
  const sort = useCallback(
    (a: T, b: T) => {
      if (!sortConfig.key) return 0;

      const valueA = getValueAtPath(a, sortConfig.key);
      const valueB = getValueAtPath(b, sortConfig.key);

      const formattedValueA = Array.isArray(valueA)
        ? valueA.sort().join(", ") || ""
        : valueA || "";
      const formattedValueB = Array.isArray(valueB)
        ? valueB.sort().join(", ") || ""
        : valueB || "";

      if (formattedValueA < formattedValueB) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (formattedValueA > formattedValueB) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    },
    [sortConfig.key, sortConfig.direction],
  );
  const filter = useCallback(
    (item: T) => {
      if (!searchQuery) return true;
      let pass = false;
      columns.forEach((column) => {
        if (column.searchKey) {
          if (Array.isArray(column.searchKey)) {
            column.searchKey.forEach((c) => {
              const value = getValueAtPath(item, c);
              if (
                value &&
                value
                  .toString()
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              ) {
                pass = true;
              }
            });
          } else {
            const value = getValueAtPath(item, column.searchKey);
            if (
              value &&
              value.toString().toLowerCase().includes(searchQuery.toLowerCase())
            ) {
              pass = true;
            }
          }
        }
      });
      return pass;
    },
    [searchQuery, columns],
  );

  return (
    <TableContainer sx={{ maxHeight: maxHeight || "auto", overflowY: "auto" }}>
      {display === "async" && (
        <Suspense
          fallback={<PanelTableSkeleton columns={columns} size={size} />}
        >
          <Await resolve={asyncData}>
            {(items) => (
              <Table stickyHeader size={size} aria-label={`${name} table`}>
                <TableHead>
                  <TableRow
                    sx={{
                      ...smallTableHeaderSx,
                      cursor: readonly ? "default" : "pointer",
                    }}
                  >
                    {columns &&
                      columns.map((column, index) => (
                        <TableCell
                          key={`${column.name}${index}`}
                          align={column.align || "left"}
                          sx={{
                            ...smallTableHeaderSx,
                            cursor: readonly ? "default" : "pointer",
                          }}
                          sortDirection={
                            sortConfig.key === column.sortKey &&
                            sortConfig.direction
                          }
                        >
                          <TableSortLabel
                            active={sortConfig.key === column.sortKey}
                            direction={
                              sortConfig.key === column.sortKey
                                ? sortConfig.direction
                                : "desc"
                            }
                            onClick={() => handleSort(column.sortKey)}
                            hideSortIcon={!column.sortKey}
                          >
                            {column.name}
                            {sortConfig.key === column.sortKey ? (
                              <Box component="span" sx={visuallyHidden}>
                                {sortConfig.direction === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items
                    ?.filter(filter)
                    .sort(sort)
                    .map((item: T, index: number) => renderRow(item, index))}
                  {!hideNoOptionsText && items?.filter(filter).length === 0 && (
                    <TableRow sx={{ backgroundColor: "white" }}>
                      <TableCell colSpan={columns.length} height={100}>
                        <Typography
                          variant={"h6"}
                          align="center"
                          textTransform={"capitalize"}
                        >
                          {`${t("noOptions", {
                            name,
                          })} ${t("tryDifferentSearch")}`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Await>
        </Suspense>
      )}
      {display === "data" && data && (
        <Table stickyHeader size={size} aria-label={`${name} table`}>
          <TableHead>
            <TableRow
              sx={{
                ...smallTableHeaderSx,
                cursor: readonly ? "default" : "pointer",
              }}
            >
              {columns &&
                columns.map((column, index) => (
                  <TableCell
                    sx={{
                      ...smallTableHeaderSx,
                      cursor: readonly ? "default" : "pointer",
                    }}
                    key={`${column.name}${index}`}
                    align={column.align || "left"}
                    onClick={() => handleSort(column.sortKey)}
                    sortDirection={
                      sortConfig.key === column.sortKey && sortConfig.direction
                    }
                  >
                    {column.name}
                    <TableSortLabel
                      active={sortConfig.key === column.sortKey}
                      direction={
                        sortConfig.key === column.sortKey
                          ? sortConfig.direction
                          : "desc"
                      }
                      hideSortIcon={sortConfig.key !== column.sortKey}
                      IconComponent={() =>
                        sortConfig.direction === "desc" ? (
                          <ArrowDownward
                            htmlColor={"white"}
                            fontSize={"small"}
                          />
                        ) : (
                          <ArrowUpward htmlColor={"white"} fontSize={"small"} />
                        )
                      }
                    />
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice(0, MAX_PREVIEW_ROWS)
              .filter(filter)
              .sort(sort)
              .map((item: T, index: number) => renderRow(item, index))}
            {!hideNoOptionsText && data.filter(filter).length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} height={100}>
                  <Typography
                    variant={"h6"}
                    align="center"
                    textTransform={"capitalize"}
                  >
                    {`${t("noOptions", {
                      name,
                    })} ${t("tryDifferentSearch")}`}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};

export default PanelTable;
